import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n/config";
import SeoHead from "@/components/SeoHead";
import { orgJsonLd } from "@/lib/structuredData";

// Lazy load route components for code splitting
const Index = lazy(() => import("./pages/Index"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const FeatureSearch = lazy(() => import("./pages/FeatureSearch"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const PropertyOwnerLanding = lazy(() => import("./pages/PropertyOwnerLanding"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const noIndex = import.meta.env.VITE_NO_INDEX === "true";
const baseUrl = "https://www.villesalento.com";

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <I18nextProvider i18n={i18n}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <SeoHead
          title="VilleSalento — Ville di Lusso in Salento"
          description="Ville di lusso in Salento: prenotazioni, foto, disponibilità e contatti diretti. Scopri le migliori proprietà nel cuore del Salento."
          canonical={baseUrl}
          structuredData={[orgJsonLd({ url: baseUrl, logo: `${baseUrl}/logo.svg` })]}
          noindex={noIndex}
          siteName="VilleSalento"
        />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              
              {/* Main listing page (old /search) */}
              <Route path="/ville-lusso-salento" element={<SearchResults />} />
              
              {/* Location-specific pages */}
              <Route path="/ville-lusso-salento/:slug" element={<LocationPage />} />
              
              {/* 301 Redirect from old /search to new URL */}
              <Route path="/search" element={<Navigate to="/ville-lusso-salento" replace />} />
              
              {/* Feature pages with SEO-friendly URLs */}
              <Route path="/sul-mare" element={<FeatureSearch />} />
              <Route path="/con-piscina" element={<FeatureSearch />} />
              <Route path="/pet-friendly" element={<FeatureSearch />} />
              <Route path="/bed-and-breakfast" element={<FeatureSearch />} />
              
              {/* Other pages */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/proprietari" element={<PropertyOwnerLanding />} />
              
              {/* Property detail pages with slug-based URLs - must be last */}
              <Route path="/:slug" element={<PropertyDetail />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </I18nextProvider>
);

export default App;
